"use client";

import * as React from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  type RowSelectionState,
  type PaginationState,
} from "@tanstack/react-table";
import { cn } from "../../../lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../table";
import { GroupedDataView } from "../grouped-data-view";
import { DataTablePagination } from "./data-table-pagination";
import { DataTableToolbar } from "./data-table-toolbar";
import { DataTableLoading } from "./data-table-loading";
import { DataTableGroupingControls } from "./data-table-grouping-controls";
import type {
  DataTableProps,
  DataTableRef,
  DataTableColumnMeta,
  DataTableViewMode,
} from "./types";

function DataTableInner<TData>(
  {
    columns,
    data,
    enableSorting = false,
    enableGlobalFilter = false,
    enablePagination = false,
    enableRowSelection = false,
    enableColumnVisibility = false,
    pageSize = 10,
    pageSizeOptions = [10, 20, 50, 100],
    manualPagination = false,
    pageCount,
    // Controlled state
    sorting: controlledSorting,
    onSortingChange,
    globalFilter: controlledGlobalFilter,
    onGlobalFilterChange,
    pagination: controlledPagination,
    onPaginationChange,
    rowSelection: controlledRowSelection,
    onRowSelectionChange,
    columnVisibility: controlledColumnVisibility,
    onColumnVisibilityChange,
    initialState,
    // Row options
    getRowId,
    onRowClick,
    isRowSelectable,
    // Search
    searchPlaceholder = "Search...",
    searchDebounceMs = 300,
    // States
    isLoading = false,
    loadingRows = 10,
    emptyState,
    // Styling
    className,
    containerClassName,
    maxHeight,
    // Grouping
    groupableBy,
    defaultViewMode = "flat",
    viewMode: controlledViewMode,
    onViewModeChange,
    defaultGroupBy,
    groupBy: controlledGroupBy,
    onGroupByChange,
    groupedStickyColumns = 0,
    groupedDefaultExpanded = true,
    renderGroupingControls = true,
  }: DataTableProps<TData>,
  ref: React.ForwardedRef<DataTableRef<TData>>,
) {
  // Internal state
  const [internalSorting, setInternalSorting] = React.useState<SortingState>(
    initialState?.sorting ?? [],
  );
  const [internalGlobalFilter, setInternalGlobalFilter] = React.useState(
    initialState?.globalFilter ?? "",
  );
  const [internalPagination, setInternalPagination] =
    React.useState<PaginationState>(
      initialState?.pagination ?? { pageIndex: 0, pageSize },
    );
  const [internalRowSelection, setInternalRowSelection] =
    React.useState<RowSelectionState>(initialState?.rowSelection ?? {});
  const [internalColumnVisibility, setInternalColumnVisibility] =
    React.useState<VisibilityState>(initialState?.columnVisibility ?? {});

  // Use controlled or internal
  const sorting = controlledSorting ?? internalSorting;
  const globalFilter = controlledGlobalFilter ?? internalGlobalFilter;
  const pagination = controlledPagination ?? internalPagination;
  const rowSelection = controlledRowSelection ?? internalRowSelection;
  const columnVisibility =
    controlledColumnVisibility ?? internalColumnVisibility;

  const handleSortingChange = onSortingChange ?? setInternalSorting;
  const handleGlobalFilterChange =
    onGlobalFilterChange ?? setInternalGlobalFilter;
  const handlePaginationChange = onPaginationChange ?? setInternalPagination;
  const handleRowSelectionChange =
    onRowSelectionChange ?? setInternalRowSelection;
  const handleColumnVisibilityChange =
    onColumnVisibilityChange ?? setInternalColumnVisibility;

  const hasGrouping = !!groupableBy && groupableBy.length > 0;

  const [internalViewMode, setInternalViewMode] =
    React.useState<DataTableViewMode>(defaultViewMode);
  const viewMode = controlledViewMode ?? internalViewMode;
  const handleViewModeChange = (next: DataTableViewMode) => {
    if (onViewModeChange) onViewModeChange(next);
    else setInternalViewMode(next);
  };

  const initialGroupBy =
    defaultGroupBy ?? (hasGrouping ? groupableBy![0].id : undefined);
  const [internalGroupBy, setInternalGroupBy] = React.useState<
    Extract<keyof TData, string> | undefined
  >(initialGroupBy);
  const groupBy = controlledGroupBy ?? internalGroupBy;
  const handleGroupByChange = (next: Extract<keyof TData, string>) => {
    if (onGroupByChange) onGroupByChange(next);
    else setInternalGroupBy(next);
  };

  const activeGroupable =
    hasGrouping && groupBy
      ? groupableBy!.find((g) => g.id === groupBy) ?? groupableBy![0]
      : undefined;
  const activeGroupBy = activeGroupable?.id;
  const isGrouped = hasGrouping && viewMode === "grouped" && !!activeGroupBy;

  const table = useReactTable({
    data,
    columns: columns as ColumnDef<TData, unknown>[],
    state: {
      sorting,
      globalFilter,
      pagination,
      rowSelection,
      columnVisibility,
    },
    getCoreRowModel: getCoreRowModel(),
    ...(enableSorting && {
      onSortingChange: handleSortingChange,
      getSortedRowModel: getSortedRowModel(),
    }),
    ...(enableGlobalFilter && {
      onGlobalFilterChange: handleGlobalFilterChange,
      getFilteredRowModel: getFilteredRowModel(),
    }),
    ...(enablePagination && {
      onPaginationChange: handlePaginationChange,
      getPaginationRowModel: getPaginationRowModel(),
      manualPagination,
      pageCount,
    }),
    ...(enableRowSelection && {
      onRowSelectionChange: handleRowSelectionChange,
      enableRowSelection: isRowSelectable ?? true,
    }),
    ...(enableColumnVisibility && {
      onColumnVisibilityChange: handleColumnVisibilityChange,
    }),
    ...(getRowId && { getRowId }),
  });

  const { rows } = table.getRowModel();

  // Imperative handle
  React.useImperativeHandle(ref, () => ({
    getTable: () => table,
    resetFilters: () => {
      table.resetColumnFilters();
      handleGlobalFilterChange("");
    },
    resetSorting: () => table.resetSorting(),
    resetSelection: () => table.resetRowSelection(),
    getSelectedRows: () =>
      table.getFilteredSelectedRowModel().rows.map((row) => row.original),
  }));

  const showToolbar =
    enableGlobalFilter ||
    enableColumnVisibility ||
    (hasGrouping && renderGroupingControls);

  const filteredForGrouping = React.useMemo(() => {
    if (!isGrouped || !enableGlobalFilter || !globalFilter) return data;
    const q = String(globalFilter).toLowerCase();
    return data.filter((row) =>
      columns.some((c) => {
        const key = (c as { accessorKey?: string }).accessorKey;
        if (!key) return false;
        const v = (row as Record<string, unknown>)[key];
        return v != null && String(v).toLowerCase().includes(q);
      }),
    );
  }, [isGrouped, data, columns, enableGlobalFilter, globalFilter]);

  const showAutoGroupingControls = hasGrouping && renderGroupingControls;
  const autoGroupingControls = showAutoGroupingControls ? (
    <DataTableGroupingControls
      className="ml-auto"
      groupableBy={groupableBy!}
      viewMode={viewMode}
      onViewModeChange={handleViewModeChange}
      groupBy={activeGroupBy}
      onGroupByChange={handleGroupByChange}
    />
  ) : null;

  return (
    <div className={cn("flex min-h-0 flex-col", className)}>
      {showToolbar && (
        <DataTableToolbar
          table={table}
          searchPlaceholder={searchPlaceholder}
          searchDebounceMs={searchDebounceMs}
          enableSearch={enableGlobalFilter}
          enableColumnVisibility={enableColumnVisibility && !isGrouped}
        >
          {autoGroupingControls}
        </DataTableToolbar>
      )}

      {isGrouped ? (
        <GroupedDataView
          columns={columns}
          data={filteredForGrouping}
          groupBy={activeGroupBy!}
          enableSelection={enableRowSelection}
          enableSorting={enableSorting}
          stickyColumns={groupedStickyColumns}
          onRowClick={onRowClick}
          getRowId={getRowId}
          formatGroupLabel={activeGroupable?.format}
          defaultExpanded={groupedDefaultExpanded}
          emptyState={emptyState}
          maxHeight={
            typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight
          }
          className={containerClassName}
        />
      ) : (
      <div
        className={cn(
          "relative min-h-0 overflow-auto",
          containerClassName,
        )}
        style={{ maxHeight: maxHeight ?? undefined }}
      >
        <Table>
          <TableHeader className="sticky top-0 z-30 bg-panel">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                className="hover:bg-transparent dark:hover:bg-transparent"
              >
                {headerGroup.headers.map((header) => {
                  const meta = header.column.columnDef.meta as
                    | DataTableColumnMeta
                    | undefined;
                  return (
                    <TableHead
                      key={header.id}
                      style={{ minWidth: header.getSize() }}
                      className={cn(
                        "bg-panel",
                        meta?.headerClassName,
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {isLoading ? (
              <DataTableLoading columns={columns.length} rows={loadingRows} />
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {emptyState ?? (
                    <div className="flex flex-col items-center justify-center py-8 text-xs text-text-tertiary">
                      <p>No results found.</p>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  onClick={
                    onRowClick ? (e) => onRowClick(row.original, e) : undefined
                  }
                  className={cn("group", onRowClick && "cursor-pointer")}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta as
                      | DataTableColumnMeta
                      | undefined;
                    return (
                      <TableCell
                        key={cell.id}
                        style={{ minWidth: cell.column.getSize() }}
                        className={cn(
                          meta?.cellClassName,
                          meta?.align === "center" && "text-center",
                          meta?.align === "right" && "text-right",
                          meta?.truncate && "truncate max-w-0",
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      )}

      {enablePagination && !isGrouped && !isLoading && rows.length > 0 && (
        <DataTablePagination table={table} pageSizeOptions={pageSizeOptions} />
      )}
    </div>
  );
}

export const DataTable = React.forwardRef(DataTableInner) as <TData>(
  props: DataTableProps<TData> & {
    ref?: React.ForwardedRef<DataTableRef<TData>>;
  },
) => React.ReactElement;

(DataTable as React.FC).displayName = "DataTable";
